/**
 * The Strategy tab's stint chart (plan item 5b): each car's stints as bars on one race clock
 * (stints-on-clock.ts), coloured by green-flag pace against the class's best (as the table's stint
 * map was) or by driver, with the stops between them, drive-throughs, and the race's cautions.
 *
 * This file holds what the chart and its PDF page draw, worked out once and tested: the rows, the
 * bars and their colours, the time ticks and labels, and the zoom window a pinch or a drag makes.
 */
import type { AnnotationData, RaceChartData } from "@shared/types";
import { carNo } from "./car-number";
import { stintsOnClock, type ClockStint } from "./stints-on-clock";
import { paceRgb } from "./strategy-view";

type Rgb = [number, number, number];

export type GanttColorBy = "pace" | "driver";

const COLOR_KEY = "rt.ganttColor";
/** How this viewer last coloured the chart (the PDF prints it the same way). */
export function readColorBy(): GanttColorBy {
  try {
    return localStorage.getItem(COLOR_KEY) === "driver" ? "driver" : "pace";
  } catch {
    return "pace";
  }
}
export function writeColorBy(v: GanttColorBy): void {
  try {
    localStorage.setItem(COLOR_KEY, v);
  } catch {
    /* private window: for this visit only */
  }
}

/** The drivers of a car in the order they first drove: blue, violet, teal, pink, then sand. */
export const DRIVER_COLORS: Rgb[] = [
  [57, 135, 229],
  [144, 133, 233],
  [43, 181, 200],
  [224, 92, 154],
  [212, 182, 44],
];
/** A stint whose driver the timing doesn't name. */
export const NO_DRIVER: Rgb = [110, 110, 136];

export interface GanttBar {
  stint: ClockStint;
  /** Average green-flag lap (s): not a pit lap, not a caution lap, under the race's green-pace cutoff. 0 without one. */
  pace: number;
  /** Laps run under a caution (pit laps left out). */
  cautionLaps: number;
  /** The car's nth driver (0 = its first), by first stint; null when the timing names nobody. */
  driverIdx: number | null;
}

export interface GanttRow {
  key: string;
  num: number;
  /** As published: "#60". */
  label: string;
  team: string;
  cls: string;
  classPos: number;
  focus: boolean;
  bars: GanttBar[];
  driveThroughs: { lap: number; atS: number }[];
  /** The class's best average green-flag pace, for its bars' colour. */
  best: number;
  /** Its drivers in the order they first drove. */
  drivers: string[];
}

export interface GanttModel {
  rows: GanttRow[];
  cautions: { startS: number; endS: number }[];
  /** The race clock's end: the latest crossing (s). */
  endS: number;
  /** Some stint names its driver (else colouring by driver says nothing). */
  hasDrivers: boolean;
  source: "package" | "timing" | "mixed";
}

/** Each car's average green-flag lap, as the Strategy table's Avg pace (strategy-engine). */
function carPace(
  data: RaceChartData,
  key: string,
  fcy: Set<number>,
  from = -Infinity,
  to = Infinity
): { pace: number; caution: number } {
  let sum = 0;
  let n = 0;
  let caution = 0;
  for (const l of data.cars[key]?.laps ?? []) {
    if (l.l < from || l.l > to || l.pit !== 0) continue;
    if (fcy.has(l.l)) {
      caution++;
      continue;
    }
    if (l.ltSec <= 1 || l.ltSec >= data.greenPaceCutoff) continue;
    sum += l.ltSec;
    n++;
  }
  return { pace: n ? sum / n : 0, caution };
}

function fcyLaps(data: RaceChartData): Set<number> {
  const s = new Set<number>();
  for (const [a, b] of data.fcy ?? []) for (let l = a; l <= b; l++) s.add(l);
  return s;
}

/**
 * The chart's rows: `cars` (chart keys) in the order given, the focus car first when it is among
 * them. A laptop gives the class in finishing order; a phone the focus and compare cars.
 */
export function ganttModel(
  data: RaceChartData,
  annotations: AnnotationData | null,
  opts: { cars: string[]; focusKey: string | null; live?: boolean }
): GanttModel {
  const clock = stintsOnClock(data, annotations, { live: opts.live });
  const fcy = fcyLaps(data);
  const bestByClass = new Map<string, number>();
  for (const [k, c] of Object.entries(data.cars)) {
    const p = carPace(data, k, fcy).pace;
    if (p > 0 && p < (bestByClass.get(c.cls) ?? Infinity)) bestByClass.set(c.cls, p);
  }
  const keys = [...new Set(opts.cars)].filter((k) => clock.cars.has(k));
  if (opts.focusKey && keys.includes(opts.focusKey)) {
    keys.splice(keys.indexOf(opts.focusKey), 1);
    keys.unshift(opts.focusKey);
  }
  const sources = new Set<string>();
  const rows = keys.map((k): GanttRow => {
    const c = data.cars[k];
    const on = clock.cars.get(k)!;
    sources.add(on.source);
    const drivers: string[] = [];
    for (const s of on.stints)
      if (s.driver && !drivers.includes(s.driver)) drivers.push(s.driver);
    return {
      key: k,
      num: c.num,
      label: `#${carNo(data, k)}`,
      team: c.team,
      cls: c.cls,
      classPos: c.finishPosClass,
      focus: k === opts.focusKey,
      bars: on.stints.map((s) => {
        const { pace, caution } = carPace(data, k, fcy, s.startLap, s.endLap);
        return {
          stint: s,
          pace,
          cautionLaps: caution,
          driverIdx: s.driver ? drivers.indexOf(s.driver) : null,
        };
      }),
      driveThroughs: on.driveThroughs,
      best: bestByClass.get(c.cls) ?? 0,
      drivers,
    };
  });
  return {
    rows,
    cautions: clock.cautions,
    endS: clock.endS,
    hasDrivers: rows.some((r) => r.drivers.length > 0),
    source: sources.size > 1 ? "mixed" : sources.has("package") ? "package" : "timing",
  };
}

/** A bar's colour: its pace against the class's best, or its driver. */
export function barRgb(
  bar: GanttBar,
  row: Pick<GanttRow, "best">,
  by: GanttColorBy
): Rgb {
  if (by === "driver")
    return bar.driverIdx === null
      ? NO_DRIVER
      : DRIVER_COLORS[Math.min(bar.driverIdx, DRIVER_COLORS.length - 1)];
  return paceRgb(bar.pace, row.best);
}

/** Race time as the chart's axis prints it: "0:00", "1:30", "12:05". */
export function clockText(s: number): string {
  const m = Math.max(0, Math.round(s / 60));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
}

/** A span of time in words: "47 min", "1 h 05 min", "35 s". */
export function spanText(s: number): string {
  if (s < 60) return `${Math.max(0, Math.round(s))} s`;
  const m = Math.round(s / 60);
  return m < 60
    ? `${m} min`
    : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")} min`;
}

const STEPS = [60, 120, 300, 600, 900, 1800, 3600, 7200, 10800, 21600];

/** Tick times (s) across a window, at a round step that leaves `minPx` or more between labels. */
export function timeTicks([t0, t1]: TimeWindow, widthPx: number, minPx = 64): number[] {
  const span = t1 - t0;
  if (!(span > 0) || !(widthPx > 0)) return [];
  const step =
    STEPS.find((s) => (s / span) * widthPx >= minPx) ?? STEPS[STEPS.length - 1];
  const out: number[] = [];
  for (let t = Math.ceil(t0 / step) * step; t <= t1 + 1e-6; t += step) out.push(t);
  return out;
}

/** Which bar a race time falls in on a row (its stint), or null in a gap between stints. */
export function barAt(row: GanttRow, s: number): GanttBar | null {
  return row.bars.find((b) => s >= b.stint.startS && s <= b.stint.endS) ?? null;
}

// ─── Zoom: a window of race time ─────────────────────────────────────────────

export type TimeWindow = [start: number, end: number];

/** The narrowest window a zoom shows. */
export const MIN_SPAN_S = 600;
/** How far a double-tap zooms in. */
export const DOUBLE_TAP_ZOOM = 3;

/** A window `span` wide starting at `start`, kept inside the race. */
export function clampWindow(start: number, span: number, endS: number): TimeWindow {
  const full = Math.max(endS, 1);
  const w = Math.min(Math.max(span, Math.min(MIN_SPAN_S, full)), full);
  const s = Math.min(Math.max(start, 0), full - w);
  return [s, s + w];
}

/** A window `span` wide with time `at` held `frac` of the way across (0 left, 1 right). */
export function zoomAt(at: number, frac: number, span: number, endS: number): TimeWindow {
  return clampWindow(at - frac * span, span, endS);
}

export function isZoomed([a, b]: TimeWindow, endS: number): boolean {
  return b - a < endS - 1;
}

/** A pinch: the window it began with, scaled by how far the fingers spread, `at` held under them. */
export function pinchWindow(
  start: TimeWindow,
  at: number,
  startDist: number,
  dist: number,
  frac: number,
  endS: number
): TimeWindow {
  return zoomAt(at, frac, (start[1] - start[0]) * (startDist / Math.max(dist, 1)), endS);
}

/** A drag `dx` pixels across a plot `w` pixels wide. */
export function panWindow(
  start: TimeWindow,
  dx: number,
  w: number,
  endS: number
): TimeWindow {
  const span = start[1] - start[0];
  return clampWindow(start[0] - (dx / Math.max(w, 1)) * span, span, endS);
}

/** A double-tap: zoomed in around `at`, or the whole race when already zoomed in. */
export function doubleTapWindow(win: TimeWindow, at: number, endS: number): TimeWindow {
  if (isZoomed(win, endS)) return [0, endS];
  const span = win[1] - win[0];
  return zoomAt(at, span > 0 ? (at - win[0]) / span : 0.5, span / DOUBLE_TAP_ZOOM, endS);
}

// ─── Words ───────────────────────────────────────────────────────────────────

const lapTime = (s: number) => {
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(3).padStart(6, "0")}`;
};

/** What a bar says when hovered or tapped: one line each. */
export function barLines(row: GanttRow, bar: GanttBar): string[] {
  const s = bar.stint;
  const laps = s.endLap - s.startLap + 1;
  const lines = [
    `${row.label} · stint ${s.n} · ${laps === 1 ? `lap ${s.startLap}` : `laps ${s.startLap}–${s.endLap}`} (${laps} lap${laps === 1 ? "" : "s"})`,
    `${clockText(s.startS)} to ${clockText(s.endS)} · ${spanText(s.endS - s.startS)}`,
  ];
  if (s.driver) lines.push(s.driver);
  const pace = [
    bar.pace > 0 ? `Green pace ${lapTime(bar.pace)}` : "",
    bar.cautionLaps
      ? `${bar.cautionLaps} caution lap${bar.cautionLaps === 1 ? "" : "s"}`
      : "",
  ].filter(Boolean);
  if (pace.length) lines.push(pace.join(" · "));
  if (s.end === "pit_in") {
    const st = s.stop;
    const what =
      st?.kind === "garage"
        ? "Into the garage"
        : st?.splash
          ? "Into the pits for a splash"
          : "Into the pits";
    const detail = [
      st?.caution ? "under caution" : "",
      st?.laneS ? `${st.laneS.toFixed(0)} s in the pit lane` : "",
      st?.lostS != null && st.lostS > 0 ? `${spanText(st.lostS)} lost` : "",
    ].filter(Boolean);
    lines.push(
      `${what} after lap ${s.endLap}${detail.length ? `: ${detail.join(", ")}` : ""}`
    );
  } else if (s.end === "chequered") lines.push("Took the flag");
  else if (s.end === "retired") lines.push("Out of the race");
  return lines;
}
